import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  IconButton,
  Image,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Icon,
  useBreakpointValue,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, ViewIcon } from "@chakra-ui/icons";
import { MdDragHandle, MdRestaurant, MdAttachMoney } from "react-icons/md";
import { getMediaURL } from "../../context/API";
import ConfirmDialog from "../../commonComponents/ConfirmDialog";

const MenuItem = ({
  item,
  categoryId,
  updateItemOrder,
  onDelete,
  onCustomizationClick,
  setCustomizations,
  setCategories,
  setEditingItem,
  setImageFile,
  setIsAddItemOpen,
}) => {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();

  // Responsive breakpoints - improved for better responsiveness
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const isTablet = useBreakpointValue({ base: false, sm: true, lg: false });
  const isDesktop = useBreakpointValue({ base: false, lg: true });

  // Color mode values
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const subtleTextColor = useColorModeValue("gray.600", "gray.300");
  const priceColor = useColorModeValue("green.600", "green.400");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const accentColor = useColorModeValue("blue.500", "blue.400");

  const handleEdit = () => {
    setEditingItem(item);
    setImageFile(null);
    setIsAddItemOpen(true);
  };

  const handleDelete = () => {
    onDelete(item.id);
    setIsDeleteOpen(false);
  };

  const handleCustomizationClick = () => {
    onCustomizationClick(item.id, { customization: item });
  };

  const getImageUrl = () => {
    if (item.picture && item.picture !== "default.jpg") {
      return getMediaURL(item.picture);
    }
    return null;
  };

  const imageUrl = getImageUrl();

  return (
    <>
      <Box
        bg={bgColor}
        border="1px solid"
        borderColor={borderColor}
        borderRadius={{ base: "lg", md: "xl" }}
        p={{ base: 3, md: 4, lg: 4 }}
        mb={{ base: 2, md: 3 }}
        shadow="sm"
        _hover={{
          shadow: "md",
          transform: "translateY(-1px)",
          borderColor: accentColor,
        }}
        transition="all 0.2s ease-in-out"
        position="relative"
        overflow="hidden"
        cursor="grab"
        _active={{ cursor: "grabbing" }}
      >
        {/* Status Badge */}
        {item.sold_out && (
          <Badge
            position="absolute"
            top={{ base: 2, md: 3 }}
            left={{ base: 2, md: 3 }}
            colorScheme="red"
            variant="solid"
            borderRadius="full"
            px={{ base: 1, md: 2 }}
            py={{ base: 0.5, md: 1 }}
            fontSize={{ base: "2xs", md: "xs" }}
            zIndex={1}
          >
            Sold Out
          </Badge>
        )}

        {/* Mobile Layout - Improved Responsiveness */}
        {isMobile ? (
          <VStack spacing={{ base: 2, sm: 3 }} align="stretch">
            {/* Image and Title Row */}
            <HStack spacing={{ base: 2, sm: 3 }} align="flex-start">
              {/* Drag Handle */}
              <Box
                cursor="grab"
                p={{ base: 1, sm: 2 }}
                borderRadius="lg"
                _active={{ cursor: "grabbing" }}
                _hover={{ bg: "gray.100" }}
                flexShrink={0}
              >
                <Icon
                  as={MdDragHandle}
                  w={{ base: 5, sm: 6 }}
                  h={{ base: 5, sm: 6 }}
                  color="gray.500"
                />
              </Box>
              <Box
                position="relative"
                flexShrink={0}
                cursor="pointer"
                onClick={onPreviewOpen}
              >
                {imageUrl ? (
                  <Image
                    src={imageUrl}
                    alt={item.item}
                    boxSize={{ base: "45px", sm: "55px" }}
                    borderRadius="md"
                    objectFit="cover"
                    border="2px solid"
                    borderColor={borderColor}
                    _hover={{
                      borderColor: accentColor,
                      transform: "scale(1.05)",
                    }}
                    transition="all 0.2s ease-in-out"
                  />
                ) : (
                  <Box
                    boxSize={{ base: "45px", sm: "55px" }}
                    borderRadius="md"
                    bg="gray.100"
                    border="2px dashed"
                    borderColor="gray.300"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    _hover={{
                      borderColor: accentColor,
                      bg: "gray.50",
                    }}
                    transition="all 0.2s ease-in-out"
                  >
                    <Icon as={MdRestaurant} boxSize={{ base: 4, sm: 5 }} color="gray.400" />
                  </Box>
                )}
                
                {/* Image Preview Indicator */}
                <Box
                  position="absolute"
                  bottom={-1}
                  right={-1}
                  bg={accentColor}
                  color="white"
                  borderRadius="full"
                  p={0.5}
                  boxSize={{ base: "14px", sm: "16px" }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="2xs"
                  opacity={0.8}
                >
                  <Icon as={ViewIcon} boxSize={{ base: 1.5, sm: 2 }} />
                </Box>
              </Box>

              <VStack align="flex-start" spacing={{ base: 0.5, sm: 1 }} flex={1} minW="0">
                <Text
                  fontSize={{ base: "sm", sm: "md" }}
                  fontWeight="700"
                  color={textColor}
                  lineHeight="1.2"
                  noOfLines={2}
                  wordBreak="break-word"
                >
                  {item.item}
                </Text>
                {item.description && (
                  <Text
                    fontSize={{ base: "2xs", sm: "xs" }}
                    color={subtleTextColor}
                    noOfLines={2}
                    wordBreak="break-word"
                  >
                    {item.description}
                  </Text>
                )}
              </VStack>

              {/* Price and Actions */}
              <VStack align="flex-end" spacing={{ base: 1, sm: 2 }} flexShrink={0}>
                <HStack spacing={1} align="center">
                  <Icon as={MdAttachMoney} color={priceColor} boxSize={{ base: 2.5, sm: 3 }} />
                  <Text
                    fontSize={{ base: "md", sm: "lg" }}
                    fontWeight="700"
                    color={priceColor}
                  >
                    {parseFloat(item.price).toFixed(2)}
                  </Text>
                </HStack>
                
                {/* Taxable Badge */}
                {item.taxable && (
                  <Badge
                    colorScheme="blue"
                    variant="subtle"
                    fontSize="2xs"
                    borderRadius="full"
                    px={1}
                    py={0.5}
                  >
                    Taxable
                  </Badge>
                )}
              </VStack>
            </HStack>

            {/* Additional Info Row - Improved for small screens */}
            <HStack spacing={{ base: 2, sm: 3 }} w="full" flexWrap="wrap" justify="center">
              {item.sequence_number && (
                <HStack spacing={1}>
                  <Text fontSize="2xs" color={subtleTextColor}>
                    Order:
                  </Text>
                  <Badge
                    colorScheme="purple"
                    variant="subtle"
                    fontSize="2xs"
                    borderRadius="full"
                    px={1}
                    py={0.5}
                  >
                    #{item.sequence_number}
                  </Badge>
                </HStack>
              )}
              
              {item.customizations && item.customizations.length > 0 && (
                <HStack spacing={1}>
                  <Text fontSize="2xs" color={subtleTextColor}>
                    Customizations:
                  </Text>
                  <Badge
                    colorScheme="green"
                    variant="subtle"
                    fontSize="2xs"
                    borderRadius="full"
                    px={1}
                    py={0.5}
                  >
                    {item.customizations.length}
                  </Badge>
                </HStack>
              )}
            </HStack>

            {/* Action Buttons Row - Improved for small screens */}
            <HStack spacing={{ base: 1, sm: 2 }} justify="center" w="full" flexWrap="wrap">
              <Button
                size={{ base: "xs", sm: "sm" }}
                leftIcon={<EditIcon />}
                variant="outline"
                colorScheme="blue"
                borderRadius="full"
                onClick={handleEdit}
                flex={{ base: "1 1 calc(50% - 4px)", sm: 1 }}
                minW={{ base: "120px", sm: "auto" }}
                _hover={{
                  bg: "blue.50",
                  transform: "scale(1.02)",
                }}
                transition="all 0.2s ease-in-out"
              >
                Edit
              </Button>

              <Button
                size={{ base: "xs", sm: "sm" }}
                leftIcon={<DeleteIcon />}
                variant="outline"
                colorScheme="red"
                borderRadius="full"
                onClick={() => setIsDeleteOpen(true)}
                flex={{ base: "1 1 calc(50% - 4px)", sm: 1 }}
                minW={{ base: "120px", sm: "auto" }}
                _hover={{
                  bg: "red.50",
                  transform: "scale(1.02)",
                }}
                transition="all 0.2s ease-in-out"
              >
                Delete
              </Button>

              {item.customizations && item.customizations.length > 0 && (
                <Button
                  size={{ base: "xs", sm: "sm" }}
                  variant="ghost"
                  colorScheme="purple"
                  borderRadius="full"
                  onClick={handleCustomizationClick}
                  flex={{ base: "1 1 100%", sm: 1 }}
                  minW={{ base: "120px", sm: "auto" }}
                  _hover={{
                    bg: "purple.50",
                    transform: "scale(1.02)",
                  }}
                  transition="all 0.2s ease-in-out"
                >
                  Customize
                </Button>
              )}
            </HStack>
          </VStack>
        ) : (
          /* Desktop/Tablet Layout - Improved Responsiveness */
          <Flex 
            align="center" 
            gap={{ base: 2, sm: 3, md: 4, lg: 4 }}
            direction={{ base: "column", sm: "row" }}
            w="full"
          >
            <Box
              cursor="grab"
              p={2}
              borderRadius="lg"
              _active={{ cursor: "grabbing" }}
              _hover={{ bg: "gray.100" }}
              flexShrink={0}
            >
              <Icon
                as={MdDragHandle}
                w={{ base: 5, sm: 5, md: 6 }}
                h={{ base: 5, sm: 5, md: 6 }}
                color="gray.500"
              />
            </Box>
            {/* Image Section - Improved Responsiveness */}
            <Box
              position="relative"
              flexShrink={0}
              cursor="pointer"
              onClick={onPreviewOpen}
              alignSelf={{ base: "center", sm: "flex-start" }}
            >
              {imageUrl ? (
                <Image
                  src={imageUrl}
                  alt={item.item}
                  boxSize={{ base: "60px", sm: "50px", md: "60px", lg: "60px" }}
                  borderRadius="lg"
                  objectFit="cover"
                  border="2px solid"
                  borderColor={borderColor}
                  _hover={{
                    borderColor: accentColor,
                    transform: "scale(1.05)",
                  }}
                  transition="all 0.2s ease-in-out"
                />
              ) : (
                <Box
                  boxSize={{ base: "60px", sm: "50px", md: "60px", lg: "60px" }}
                  borderRadius="lg"
                  bg="gray.100"
                  border="2px dashed"
                  borderColor="gray.300"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  _hover={{
                    borderColor: accentColor,
                    bg: "gray.50",
                  }}
                  transition="all 0.2s ease-in-out"
                >
                  <Icon as={MdRestaurant} boxSize={{ base: 6, sm: 5, md: 6, lg: 6 }} color="gray.400" />
                </Box>
              )}
              
              {/* Image Preview Indicator */}
              <Box
                position="absolute"
                bottom={-2}
                right={-2}
                bg={accentColor}
                color="white"
                borderRadius="full"
                p={1}
                boxSize={{ base: "18px", sm: "16px", md: "20px" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="xs"
                opacity={0.8}
              >
                <Icon as={ViewIcon} boxSize={{ base: 2.5, sm: 2, md: 3 }} />
              </Box>
            </Box>

            {/* Content Section - Improved Responsiveness */}
            <VStack align="flex-start" spacing={{ base: 1, sm: 2 }} flex={1} minW="0">
              <HStack justify="space-between" w="full" align="flex-start" flexWrap="wrap">
                <VStack align="flex-start" spacing={{ base: 0.5, sm: 1 }} flex={1} minW="0">
                  <Text
                    fontSize={{ base: "sm", sm: "md", md: "lg", lg: "lg" }}
                    fontWeight="700"
                    color={textColor}
                    lineHeight="1.2"
                    noOfLines={2}
                    wordBreak="break-word"
                  >
                    {item.item}
                  </Text>
                  {item.description && (
                    <Text
                      fontSize={{ base: "2xs", sm: "xs", md: "sm", lg: "sm" }}
                      color={subtleTextColor}
                      noOfLines={2}
                      maxW={{ base: "150px", sm: "200px", md: "300px", lg: "300px" }}
                      wordBreak="break-word"
                    >
                      {item.description}
                    </Text>
                  )}
                </VStack>

                {/* Price Section - Improved Responsiveness */}
                <VStack align="flex-end" spacing={{ base: 1, sm: 2 }} flexShrink={0}>
                  <HStack spacing={{ base: 1, sm: 2 }} align="center">
                    <Icon as={MdAttachMoney} color={priceColor} boxSize={{ base: 3, sm: 4 }} />
                    <Text
                      fontSize={{ base: "md", sm: "lg", md: "xl", lg: "xl" }}
                      fontWeight="700"
                      color={priceColor}
                    >
                      {parseFloat(item.price).toFixed(2)}
                    </Text>
                  </HStack>
                  
                  {/* Taxable Badge */}
                  {item.taxable && (
                    <Badge
                      colorScheme="blue"
                      variant="subtle"
                      fontSize={{ base: "2xs", sm: "xs", md: "xs", lg: "xs" }}
                      borderRadius="full"
                      px={{ base: 1, sm: 2 }}
                      py={{ base: 0.5, sm: 1 }}
                    >
                      Taxable
                    </Badge>
                  )}
                </VStack>
              </HStack>

              {/* Additional Info - Improved Responsiveness */}
              <HStack spacing={{ base: 2, sm: 3, md: 4 }} w="full" flexWrap="wrap">
                {item.sequence_number && (
                  <HStack spacing={1}>
                    <Text fontSize={{ base: "2xs", sm: "xs", md: "xs", lg: "xs" }} color={subtleTextColor}>
                      Order:
                    </Text>
                    <Badge
                      colorScheme="purple"
                      variant="subtle"
                      fontSize={{ base: "2xs", sm: "xs", md: "xs", lg: "xs" }}
                      borderRadius="full"
                      px={{ base: 1, sm: 2 }}
                      py={{ base: 0.5, sm: 1 }}
                    >
                      #{item.sequence_number}
                    </Badge>
                  </HStack>
                )}
                
                {item.customizations && item.customizations.length > 0 && (
                  <HStack spacing={1}>
                    <Text fontSize={{ base: "2xs", sm: "xs", md: "xs", lg: "xs" }} color={subtleTextColor}>
                      Customizations:
                    </Text>
                    <Badge
                      colorScheme="green"
                      variant="subtle"
                      fontSize={{ base: "2xs", sm: "xs", md: "xs", lg: "xs" }}
                      borderRadius="full"
                      px={{ base: 1, sm: 2 }}
                      py={{ base: 0.5, sm: 1 }}
                    >
                      {item.customizations.length}
                    </Badge>
                  </HStack>
                )}
              </HStack>
          </VStack>

          {/* Action Buttons - Improved Responsiveness */}
          <VStack spacing={{ base: 1, sm: 2 }} align="center" flexShrink={0} w={{ base: "full", sm: "auto" }} mt={{ base: 0, sm: item.sold_out ? 6 : 0 }}>
            <HStack spacing={{ base: 1, sm: 2 }} w={{ base: "full", sm: "auto" }} justify={{ base: "center", sm: "flex-start" }}>
              <Tooltip label="Edit item" placement="top">
                <IconButton
                  aria-label="Edit item"
                  icon={<EditIcon />}
                  size={{ base: "xs", sm: "sm", md: "md", lg: "md" }}
                  variant="outline"
                  colorScheme="blue"
                  borderRadius="full"
                  onClick={handleEdit}
                  _hover={{
                    bg: "blue.50",
                    transform: "scale(1.05)",
                  }}
                  transition="all 0.2s ease-in-out"
                />
              </Tooltip>

              <Tooltip label="Delete item" placement="top">
                <IconButton
                  aria-label="Delete item"
                  icon={<DeleteIcon />}
                  size={{ base: "xs", sm: "sm", md: "md", lg: "md" }}
                  variant="outline"
                  colorScheme="red"
                  borderRadius="full"
                  onClick={() => setIsDeleteOpen(true)}
                  _hover={{
                    bg: "red.50",
                    transform: "scale(1.05)",
                  }}
                  transition="all 0.2s ease-in-out"
                />
              </Tooltip>
            </HStack>

            {item.customizations && item.customizations.length > 0 && (
              <Tooltip label="View customizations" placement="top">
                <Button
                  size={{ base: "xs", sm: "sm", md: "sm", lg: "sm" }}
                  variant="ghost"
                  colorScheme="purple"
                  borderRadius="full"
                  onClick={handleCustomizationClick}
                  w={{ base: "full", sm: "auto" }}
                  _hover={{
                    bg: "purple.50",
                    transform: "scale(1.05)",
                  }}
                  transition="all 0.2s ease-in-out"
                >
                  Customize
                </Button>
              </Tooltip>
            )}
          </VStack>
        </Flex>
      )}
    </Box>

      {/* Image Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size={{ base: "sm", md: "md", lg: "lg" }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize={{ base: "md", md: "lg" }}>Image Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {imageUrl ? (
              <Image
                src={imageUrl}
                alt={item.item}
                w="full"
                borderRadius="lg"
                objectFit="cover"
              />
            ) : (
              <Box
                w="full"
                h={{ base: "150px", md: "200px", lg: "200px" }}
                bg="gray.100"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                border="2px dashed"
                borderColor="gray.300"
              >
                <VStack spacing={2}>
                  <Icon as={MdRestaurant} boxSize={{ base: 8, md: 12, lg: 12 }} color="gray.400" />
                  <Text color="gray.500" fontSize={{ base: "sm", md: "md" }}>No image available</Text>
                </VStack>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onPreviewClose} colorScheme="blue" size={{ base: "sm", md: "md" }}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete Item"
        message={`Are you sure you want to delete "${item.item}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColorScheme="red"
      />
    </>
  );
};

export default MenuItem;
